import { useMemo, useState } from 'react';
import type { DeliveryOrderRowDto } from '@matix/api-client';
import { useLang } from '../../lib/language';
import { moneyPrecision } from '../../store/currency';
import { useCurrency } from '../print/useCurrency';
import { useReprintReceipt } from '../print';
import { PaymentMethodEditDialog } from '../shift/PaymentMethodEditDialog';
import { formatReceiptDateTime, formatReceiptTime } from '../receipts/helpers';
import { useDeliveryOrders, useDebounced, type DeliveryFilter } from './useDeliveryOrders';
import { DeliverySettleDialog } from './DeliverySettleDialog';
import { ShipmentChip, ShipmentDialog, useShipmentSummaries, useShippingProviders } from '../shipping';

/**
 * The Delivery workspace.
 *
 * WHY IT EXISTS. A delivery order rung up at the till is an ordinary POS
 * receipt with `OrderType = 2` and NO `cc_order` row. When the kitchen presses
 * تسليم on it, the KDS opens its driver dialog (gated on the order type alone),
 * stamps `Inv_Receipt_h.DriverId`, and the ticket leaves the board — while the
 * branch Pending Orders screen only ever listed `cc_order` rows and is hidden
 * entirely unless the Call Center is switched on. The order had nowhere left to
 * appear, and nobody could take the money for it.
 *
 * This screen reads RECEIPTS, so it shows a phone order and a till order side
 * by side, and it is NOT gated on the call centre.
 *
 * TWO ACTIONS, DELIBERATELY DISTINCT:
 *
 *   Take payment        — the money has not been recorded yet. Writes the
 *                         tender, the split slices and the settling cashier's
 *                         shift. `POST /delivery-orders/:id/settle`.
 *   Change payment type — the money IS recorded and the tender is wrong (the
 *                         till books a delivery receipt at CREATE, before the
 *                         food is cooked, so the header is a guess until the
 *                         driver comes back). Writes an immutable
 *                         `payment_method_audit` row, through the dialog the
 *                         shift-close drill-down already uses.
 *
 * They stay separate because they are different writes: a settle records money
 * for the first time AND stamps the settling shift, while a correction only
 * relabels a tender that is already booked.
 *
 * A CALL-CENTRE order is listed but never settled here — `has_call_centre_order`
 * sends the cashier to Pending Orders, because that screen closes the phone
 * order out too, and settling in both places would double the drawer.
 */
export function DeliveryOrdersScreen(): JSX.Element {
  const { t, lang } = useLang();
  const currency = useCurrency();
  const precision = moneyPrecision();
  const reprint = useReprintReceipt();

  const [filter, setFilter] = useState<DeliveryFilter>('unpaid');
  const [search, setSearch] = useState('');
  const [settleTarget, setSettleTarget] = useState<DeliveryOrderRowDto | null>(null);
  const [retenderTarget, setRetenderTarget] = useState<DeliveryOrderRowDto | null>(null);

  // Debounced so a cashier typing a phone number fires one request, not ten —
  // each of which would also tear down and recreate the poll interval.
  const debouncedSearch = useDebounced(search);
  const params = useMemo(
    () => ({
      filter,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      limit: 200,
    }),
    [filter, debouncedSearch],
  );
  const live = useDeliveryOrders(params);

  // Shipping companies: shown only when the admin has activated at least one
  // (an empty list hides every trace of the feature), and one batched call
  // per poll tells each card where its parcel stands.
  const providersQ = useShippingProviders();
  const providers = providersQ.data ?? [];
  const shippingOn = providers.length > 0;
  const orderIds = useMemo(() => live.orders.map((o) => o.id), [live.orders]);
  const shipmentsQ = useShipmentSummaries(orderIds, shippingOn);
  const shipments = shipmentsQ.data ?? {};
  const [shipTarget, setShipTarget] = useState<DeliveryOrderRowDto | null>(null);

  const money = (n: number) =>
    `${(Number.isFinite(n) ? n : 0).toFixed(Math.max(0, Math.min(8, precision)))} ${currency.code}`;

  // `Entry_Date` is a NAIVE tenant wall-clock, and the API sends its digits
  // with a trailing 'Z' that does not mean UTC. `toLocaleTimeString` would
  // re-read those digits as UTC and shift every hand-over time by the tenant's
  // offset — a 07:11 order in Riyadh rendering as 10:11. `formatReceiptTime`
  // takes the digits literally, which is what the Receipts screen and the
  // printed receipt already do.
  const timeOf = (iso: string | null): string => (iso ? formatReceiptTime(iso) : '');

  return (
    <div className="cc-screen cc-pending">
      <header className="cc-pending-head">
        <div className="cc-tabs">
          {(['unpaid', 'paid', 'all'] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`cc-tab ${filter === f ? 'cc-tab--on' : ''}`}
              onClick={() => setFilter(f)}
            >
              {t(
                {
                  unpaid: { en: 'Awaiting payment', ar: 'بانتظار التحصيل' },
                  paid: { en: 'Paid today', ar: 'المحصّلة اليوم' },
                  all: { en: 'All today', ar: 'كل طلبات اليوم' },
                }[f],
              )}
            </button>
          ))}
        </div>
        <div className="cc-pending-status">
          {/*
            Say so when the page does not hold everything. "Awaiting payment"
            is unbounded by date and sorted newest-first, so past the page size
            it is the orders uncollected LONGEST that fall off the bottom —
            silently, and they are the ones that most need chasing.
          */}
          {live.total > live.orders.length && (
            <span className="cc-chip cc-chip--unpaid">
              {t({
                en: `Showing ${live.orders.length} of ${live.total} — narrow with search`,
                ar: `عرض ${live.orders.length} من ${live.total} — استخدم البحث للتضييق`,
              })}
            </span>
          )}
          <input
            className="cc-search"
            value={search}
            placeholder={t({ en: 'Receipt no, name or phone', ar: 'رقم الفاتورة أو الاسم أو الجوال' })}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="cc-link" onClick={live.refresh}>
            {t({ en: 'Refresh', ar: 'تحديث' })}
          </button>
        </div>
      </header>

      {live.error && <div className="cc-warn">{live.error}</div>}

      {!live.loading && live.orders.length === 0 && (
        <div className="cc-empty">
          <div className="cc-empty-title">
            {filter === 'unpaid'
              ? t({ en: 'Nothing to collect', ar: 'لا يوجد ما يُحصّل' })
              : t({ en: 'No delivery orders', ar: 'لا توجد طلبات توصيل' })}
          </div>
          <p>
            {filter === 'unpaid'
              ? t({
                  en: 'Delivery orders whose money has not been taken yet appear here — from the till and from the phone alike — and stay until someone collects.',
                  ar: 'تظهر هنا طلبات التوصيل التي لم يتم تحصيل قيمتها بعد — من الكاشير ومن الهاتف على حد سواء — وتبقى حتى يتم التحصيل.',
                })
              : t({
                  en: 'Delivery orders for this branch appear here as soon as they are rung up.',
                  ar: 'تظهر طلبات التوصيل لهذا الفرع هنا فور تسجيلها.',
                })}
          </p>
        </div>
      )}

      <div className="cc-grid">
        {live.orders.map((order) => {
          const who =
            (lang === 'ar' ? order.customer_name_ar || order.customer_name : order.customer_name) ||
            t({ en: 'Walk-in', ar: 'عميل نقدي' });
          const driver =
            lang === 'ar' ? order.driver_name_ar || order.driver_name : order.driver_name;

          // The stripe reuses the call-centre card's own status colours: a
          // paid order is done with (grey), one the kitchen has handed to a
          // driver is out (purple), anything else is still in the shop.
          const stripe = order.is_canceled
              ? 'cc-card--cancelled'
              : order.is_paid
                ? 'cc-card--delivered'
                : order.driver_id != null || order.kitchen_done
                  ? 'cc-card--out_for_delivery'
                  : 'cc-card--preparing';

            return (
            <article key={order.id} className={`cc-card ${stripe}`}>
              <div className="cc-card-top">
                <span className="cc-card-no">#{order.entry_no ?? order.id}</span>
                <span className="cc-delay">{timeOf(order.entry_date)}</span>
              </div>

              <div className="cc-card-cust">
                <span className="cc-card-name">{who}</span>
                {order.customer_phone && (
                  <span className="cc-card-phone">{order.customer_phone}</span>
                )}
              </div>

              {/* When the customer was promised the order — the one line a
                  hand-over screen is read by. Digits verbatim, 12-hour. */}
              {order.delivery_date && (
                <div className="cc-card-deliver">
                  <span className="cc-card-deliver-label">{t({ en: 'Deliver', ar: 'التوصيل' })}</span>
                  <span className="cc-card-deliver-time">{formatReceiptDateTime(order.delivery_date)}</span>
                </div>
              )}

              <div className="cc-card-meta">
                <span className="cc-chip">
                  {t({ en: 'Items', ar: 'الأصناف' })}: {order.item_count}
                </span>
                {order.delivery_fees > 0 && (
                  <span className="cc-chip">
                    {t({ en: 'Delivery', ar: 'التوصيل' })} {money(order.delivery_fees)}
                  </span>
                )}
                <span className="cc-chip">
                  {driver
                    ? `${t({ en: 'Driver', ar: 'السائق' })}: ${driver}`
                    : t({ en: 'No driver', ar: 'بدون سائق' })}
                </span>
                {order.kitchen_done && (
                  <span className="cc-chip">{t({ en: 'Kitchen done', ar: 'المطبخ جاهز' })}</span>
                )}
              </div>

              <div className="cc-card-foot">
                <span className="cc-card-total">{money(order.net_value)}</span>
                <span className={`cc-chip ${order.is_paid ? 'cc-chip--paid' : 'cc-chip--unpaid'}`}>
                  {order.is_paid
                    ? order.payment_name || t({ en: 'Paid', ar: 'مدفوع' })
                    : t({ en: 'UNPAID', ar: 'غير مدفوع' })}
                </span>
              </div>

              <div className="cc-card-actions">
                {/*
                  A phone order is settled on Pending Orders and ONLY there:
                  that screen also closes out the `cc_order`, whose own guard is
                  `settled_at`. Settling here would leave that guard open and
                  let the same money be taken twice. Say where to go rather than
                  offering a button the server will refuse.
                */}
                {!order.is_paid && !order.is_canceled && order.has_call_centre_order && (
                  <span className="cc-chip">
                    {t({ en: 'Settle on Pending orders', ar: 'التحصيل من شاشة الطلبات قيد الانتظار' })}
                  </span>
                )}

                {!order.is_paid && !order.is_canceled && !order.has_call_centre_order && (
                  <button
                    type="button"
                    className="cc-btn cc-btn--primary"
                    onClick={() => setSettleTarget(order)}
                  >
                    {t({ en: 'Take payment', ar: 'تحصيل الدفع' })}
                  </button>
                )}

                {/*
                  A split header sits on the Multi sentinel, and the
                  payment-method patch refuses to move away from it — the slices
                  underneath would no longer add up to the header. Offering the
                  button there would only ever produce an error.
                */}
                {order.is_paid && !order.is_split && !order.is_canceled && (
                  <button
                    type="button"
                    className="cc-btn"
                    onClick={() => setRetenderTarget(order)}
                  >
                    {t({ en: 'Change payment type', ar: 'تغيير طريقة الدفع' })}
                  </button>
                )}

                <button type="button" className="cc-btn" onClick={() => void reprint(order.id)}>
                  {t({ en: 'Print', ar: 'طباعة' })}
                </button>

                {/*
                  Shipping company (Rule 3: delivery orders only — every card
                  here is one). The chip carries the courier's logo and the
                  normalised status; the button opens the panel that chooses,
                  quotes, creates, tracks or cancels. Hidden entirely while no
                  courier is activated for the company.
                */}
                {shippingOn && !order.is_canceled && (
                  shipments[order.id] ? (
                    <ShipmentChip shipment={shipments[order.id]!} onClick={() => setShipTarget(order)} />
                  ) : (
                    <button type="button" className="cc-btn" onClick={() => setShipTarget(order)}>
                      {t({ en: 'Ship with…', ar: 'شحن عبر…' })}
                    </button>
                  )
                )}
              </div>
            </article>
          );
        })}
      </div>

      {shipTarget && (
        <ShipmentDialog
          order={shipTarget}
          providers={providers}
          onClose={() => {
            setShipTarget(null);
            void shipmentsQ.refetch();
          }}
        />
      )}

      <DeliverySettleDialog
        order={settleTarget}
        onClose={() => setSettleTarget(null)}
        onSettled={(o) => {
          live.patch(o);
          live.refresh();
        }}
      />

      {retenderTarget && (
        <PaymentMethodEditDialog
          receiptId={retenderTarget.id}
          receiptCode={String(retenderTarget.entry_no ?? retenderTarget.id)}
          currentPaymentTypeId={retenderTarget.payment_type_id}
          currentPaymentLabel={retenderTarget.payment_name}
          onClose={() => setRetenderTarget(null)}
          onSaved={() => {
            setRetenderTarget(null);
            live.refresh();
          }}
        />
      )}
    </div>
  );
}
